// @flow
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { setBulkEmailStatus, startSettingBulkPreview } from 'actions/bulkEmail';
import { BulkEmailSequencesPreview } from 'features/bulk-email/sequences/components/BulkEmalSequencesPreview';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import { BulkMenuItems } from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { dateFormatter, hasFeatureFlag } from 'UI/utils';

import { PreviewMetaRowsSections } from './components/PreviewMetaRowsSections/PreviewMetaRowsSections';
import { getMailMeta } from './utils';

const { mailMeta: MAIL_META_STRINGS } = strings.bulkEmails.preview;

export const buildDateToRender = (selectedMenuItem, createdAt, sendDate) => {
  const date = selectedMenuItem === BulkMenuItems.Drafts ? createdAt : sendDate;
  return dateFormatter(date, ({ MonthDayYearTime }) => MonthDayYearTime);
};

const PreviewBulkEmail = () => {
  const dispatch = useDispatch();
  const bulkInPreviewData = useSelector(store => store.bulkEmail.domain.bulkEmailInPreview.data);
  const {
    selectedMenuItem,
    status: { reloadEmailList }
  } = useSelector(store => store.bulkEmail.ui);
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isBlockAssociatedCompaniesActive = checkIfFeatureFlagEnabled(
    FeatureFlags.BulkEmailBlockAssociatedCompanies
  );
  const areBulkSequencesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailSequences);

  const {
    id: bulkId,
    emailBody: { html: emailBody, signature, subject }
  } = bulkInPreviewData;

  const { mainMailMeta, secondaryMailMeta, sequencesMeta } = getMailMeta({
    bulkInPreviewData,
    selectedMenuItem,
    isBlockAssociatedCompaniesActive,
    isSequencesActive: areBulkSequencesEnabled
  });
  const getBodyContent = useCallback(() => {
    if (!emailBody) {
      return MAIL_META_STRINGS.emailBody.defaultValue;
    }

    // TODO: can this feature flag and validation be removed already?
    return signature && hasFeatureFlag(FeatureFlags.BulkEmailEnableSignatureOnSchedules)
      ? `${emailBody}${signature}`
      : emailBody;
  }, [emailBody, signature]);

  useEffect(() => {
    if (reloadEmailList && bulkId) {
      dispatch(startSettingBulkPreview(bulkId));
      dispatch(setBulkEmailStatus({ reloadEmailList: false }));
    }
  }, [bulkId, dispatch, reloadEmailList]);

  const CommonPreviewMetaRowsSectionsProps = useMemo(
    () => ({
      emailBody: getBodyContent(),
      firstSectionData: mainMailMeta,
      secondarySectionData: secondaryMailMeta,
      subject: subject || MAIL_META_STRINGS.subject.defaultValue
    }),
    [getBodyContent, mainMailMeta, secondaryMailMeta, subject]
  );

  return (
    <Grid container style={{ padding: '24px 0' }}>
      {areBulkSequencesEnabled && sequencesMeta ? (
        <BulkEmailSequencesPreview
          initialBulkConfig={{
            id: bulkInPreviewData.id,
            typeId: bulkInPreviewData.emailConfig.bulkType.id,
            userEmail: bulkInPreviewData.userInfo.email
          }}
          initialBulkMetaRows={<PreviewMetaRowsSections {...CommonPreviewMetaRowsSectionsProps} />}
          sequencesMeta={sequencesMeta}
        />
      ) : (
        <PreviewMetaRowsSections
          {...CommonPreviewMetaRowsSectionsProps}
          isEmailBodySectionAlwaysVisible
        />
      )}
    </Grid>
  );
};

export default PreviewBulkEmail;
